import React, { useState, useEffect, useCallback } from 'react';
import { audioDiagnosticsAPI } from '../../services/api';
import { FiRefreshCw, FiActivity, FiMic, FiWifi, FiCheckCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

// Xulosalar serverda hisoblanadi (backend/src/routes/audioDiagnostics.js) — bu yerda
// faqat ko'rsatiladi, shuning uchun mezonlar ikki joyda ajralib ketmaydi.
const VERDICTS = {
  ok:      { label: 'Sog\'lom',   cls: 'badge-success', icon: FiCheckCircle },
  mic:     { label: 'Mikrofon',   cls: 'badge-danger',  icon: FiMic },
  network: { label: 'Tarmoq',     cls: 'badge-warning', icon: FiWifi },
  both:    { label: 'Ikkalasi',   cls: 'badge-danger',  icon: FiActivity },
  unknown: { label: 'Noma\'lum',  cls: 'badge-neutral', icon: FiActivity },
};

// ICE yo'li: relay = media TURN server orqali aylanib o'tyapti (ko'proq kechikish,
// tarmoqqa sezgirroq). host/srflx = to'g'ridan-to'g'ri.
const PATHS = {
  host: 'to\'g\'ridan-to\'g\'ri',
  srflx: 'NAT orqali',
  prflx: 'NAT orqali',
  relay: 'TURN orqali',
};

const n1 = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));
const n2 = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));

export default function AudioDiagnostics() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (filter) params.verdict = filter;
      const [listRes, sumRes] = await Promise.all([
        audioDiagnosticsAPI.list({ ...params, limit: 200 }),
        audioDiagnosticsAPI.summary({ days }),
      ]);
      setRecords(listRes.data.records || []);
      setSummary(sumRes.data.summary || []);
    } catch (err) {
      toast.error('Diagnostika ma\'lumotlarini olishda xatolik');
    } finally {
      setLoading(false);
    }
  }, [days, filter]);

  useEffect(() => { load(); }, [load]);

  const problems = records.filter(r => r.verdict !== 'ok' && r.verdict !== 'unknown').length;

  return (
    <div>
      <div style={styles.intro}>
        <strong>Ovoz muammosi qaysi tomondan?</strong> Har qo'ng'iroqdan keyin operator
        brauzeri o'lchovlarni shu yerga yuboradi: mijozga yetmagan paketlar, kechikish,
        jitter (tarmoq) va mikrofonning xom signali (kompyuter). Shundan keyin
        "internetmi yoki mikrofonmi" degan savolga taxmin emas, o'lchov javob beradi.
      </div>

      <div style={styles.toolbar}>
        <select className="input" style={styles.select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={1}>Bugun</option>
          <option value={7}>7 kun</option>
          <option value={30}>30 kun</option>
        </select>
        <select className="input" style={styles.select} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">Barchasi</option>
          <option value="ok">Faqat sog'lom</option>
          <option value="mic">Faqat mikrofon</option>
          <option value="network">Faqat tarmoq</option>
          <option value="both">Ikkalasi</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <FiRefreshCw size={14} /> {loading ? 'Yuklanmoqda…' : 'Yangilash'}
        </button>
        <div style={styles.count}>
          {records.length} ta qo'ng'iroq
          {problems > 0 && <span style={styles.problemCount}> · {problems} tasida muammo</span>}
        </div>
      </div>

      {/* --- Operator kesimidagi xulosa: muammo hammadami yoki bitta kompyuterdami --- */}
      <div className="card" style={styles.card}>
        <div className="card-header"><div className="card-title">Operatorlar kesimida</div></div>
        {summary.length === 0 ? (
          <div style={styles.empty}>
            Hali o'lchov yo'q. Operatorlar sahifani yangilab (Ctrl+Shift+R) bir necha
            qo'ng'iroq qilgandan keyin ma'lumot shu yerda paydo bo'ladi.
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Operator</th><th>Jami</th><th>Sog'lom</th><th>Mikrofon</th><th>Tarmoq</th>
                  <th>O'rt. kechikish</th><th>O'rt. yo'qotish</th><th>O'rt. jitter</th><th>Media yo'li</th><th>Mikrofon qurilmasi</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((s) => (
                  <tr key={s.extension || 'noma\'lum'}>
                    <td><strong>{s.full_name || '—'}</strong> <span style={styles.dim}>{s.extension}</span></td>
                    <td>{s.jami}</td>
                    <td><span className="badge badge-success">{s.soglom}</span></td>
                    <td>{s.mikrofon > 0 ? <span className="badge badge-danger">{s.mikrofon}</span> : '—'}</td>
                    <td>{s.tarmoq > 0 ? <span className="badge badge-warning">{s.tarmoq}</span> : '—'}</td>
                    <td>{n1(s.ort_rtt)} ms</td>
                    <td>{n2(s.ort_yoqotish)}%</td>
                    <td>{n1(s.ort_jitter)} ms</td>
                    <td>{PATHS[s.asosiy_yol] || s.asosiy_yol || '—'}</td>
                    <td style={styles.device} title={s.mikrofon_qurilma || ''}>{s.mikrofon_qurilma || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Har bir qo'ng'iroq --- */}
      <div className="card" style={styles.card}>
        <div className="card-header"><div className="card-title">Qo'ng'iroqlar bo'yicha</div></div>
        {records.length === 0 ? (
          <div style={styles.empty}>Ko'rsatiladigan yozuv yo'q.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Vaqt</th><th>Operator</th><th>Raqam</th><th>Davomiyligi</th><th>Xulosa</th>
                  <th>Yo'qotish (mijozga)</th><th>Kechikish</th><th>Jitter</th><th>Paket/sek</th>
                  <th>Mikrofon jimligi</th><th>Media yo'li</th><th>Versiya</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const v = VERDICTS[r.verdict] || VERDICTS.unknown;
                  return (
                    <tr key={r.id}>
                      <td style={styles.dim}>
                        {new Date(r.created_at).toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>{r.full_name || '—'} <span style={styles.dim}>{r.extension}</span></td>
                      <td>{r.remote_number || '—'}</td>
                      <td>{r.duration_sec}s</td>
                      <td>
                        <span className={`badge ${v.cls}`} title={(r.issues || []).join(' · ')}>{v.label}</span>
                      </td>
                      <td>{n2(r.loss_out_pct)}%</td>
                      <td>{n1(r.rtt_ms)} ms</td>
                      <td>{n1(r.jitter_out_ms)} ms</td>
                      <td>{n1(r.pkt_per_sec)}</td>
                      <td>{r.mic_zero_ms > 0 ? `${Math.round(r.mic_zero_ms / 1000)}s` : '—'}</td>
                      <td>{PATHS[r.candidate_type] || r.candidate_type || '—'}</td>
                      <td style={styles.dim}>{r.app_build || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={styles.legend}>
        <div><strong>Qanday o'qiladi:</strong></div>
        <div>· <strong>Mikrofon</strong> — paketlar mijozga ketgan, lekin ular JIMLIK olib borgan.
          Sabab operator kompyuterida: qurilma o'chirilgan, noto'g'ri standart mikrofon yoki
          Bluetooth garnitura noto'g'ri rejimda. Operator "Mikrofon" tugmasidan boshqa qurilmani tanlashi kerak.</div>
        <div>· <strong>Tarmoq</strong> — paketlar yo'qolgan yoki kechikkan. Sabab internet/kanal.
          "Media yo'li" ustuni <em>TURN orqali</em> desa, ovoz qo'shimcha serverdan aylanib o'tyapti.</div>
        <div>· <strong>Versiya</strong> — operator brauzeridagi bundle. Barchada bir xil bo'lishi kerak;
          farq qilsa, o'sha operator sahifani yangilamagan (Ctrl+Shift+R).</div>
      </div>
    </div>
  );
}

const styles = {
  intro: {
    padding: '14px 16px',
    background: 'var(--accent-light)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    fontSize: '13px',
    lineHeight: 1.6,
    marginBottom: '16px',
  },
  toolbar: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' },
  select: { width: 'auto', minWidth: '140px' },
  count: { marginLeft: 'auto', fontSize: '13px', color: 'var(--text-muted)' },
  problemCount: { color: 'var(--danger)', fontWeight: 600 },
  card: { marginBottom: '20px' },
  empty: { padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6 },
  dim: { color: 'var(--text-muted)', fontSize: '12px' },
  device: { maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  legend: {
    fontSize: '12.5px',
    lineHeight: 1.8,
    color: 'var(--text-secondary)',
    padding: '14px 16px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
  },
};
